import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Receipt, RefreshCcw, Download, AlertCircle, Users, Copy } from '../../ui/icons';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import Colors from '../../../constants/Colors';
import { exportToCsv } from '../../../utils/csv';
import { copyToClipboard } from '../../../utils/clipboard';
import {
  opsPurchasersApi,
  PurchaserRow,
  SchoolStat,
  PurchasersTotals,
} from '../../../services/api';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'EXPIRED', label: 'Expired' },
];

// Matches OpsPurchasersService._product_label() on the backend.
const PRODUCT_OPTIONS = ['All Sports Pass', 'Football Season Pass', 'Basketball Season Pass', 'Single Game'];

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: Colors.textLight,
  marginBottom: '6px',
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  fontSize: '12.5px',
  color: Colors.text,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

function formatPurchaseDate(row: PurchaserRow): string {
  if (!row.created_at_iso) return '—';
  const date = new Date(row.created_at_iso);
  return Number.isNaN(date.getTime()) ? row.created_at_iso : date.toLocaleString();
}

function breakdownLabel(breakdown: Record<string, number>): string {
  const entries = Object.entries(breakdown).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return 'No purchases';
  return entries.map(([label, count]) => `${label}: ${count}`).join(' · ');
}

export default function PurchasersPanel() {
  const [purchasers, setPurchasers] = useState<PurchaserRow[]>([]);
  const [stats, setStats] = useState<SchoolStat[]>([]);
  const [totals, setTotals] = useState<PurchasersTotals | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [schoolFilter, setSchoolFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [includeTest, setIncludeTest] = useState(false);
  // Empty selection = "All Pass" (no product filter). Picking any specific product implicitly
  // deselects "All Pass"; unchecking the last specific product falls back to "All Pass" again.
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const isAllPassSelected = selectedProducts.length === 0;

  const toggleProduct = (label: string) => {
    setSelectedProducts((prev) => (prev.includes(label) ? prev.filter((p) => p !== label) : [...prev, label]));
  };

  const fetchData = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) setLoading(true);
    const params = {
      status: statusFilter || undefined,
      include_test: includeTest,
      // "All Pass" (empty selection) omits the filter entirely, so rows with an unexpected
      // product_label (e.g. a fallback like "PURCHASE#football") still show up by default.
      product_labels: isAllPassSelected ? undefined : selectedProducts,
    };
    const [listResponse, statsResponse] = await Promise.all([
      opsPurchasersApi.list(params),
      opsPurchasersApi.stats(params),
    ]);

    if (listResponse.success && listResponse.data) {
      setError(null);
      setPurchasers(listResponse.data.purchasers || []);
    } else {
      setError(listResponse.error || listResponse.data?.error || 'Failed to load purchasers');
    }

    if (statsResponse.success && statsResponse.data) {
      setStats(statsResponse.data.stats || []);
      setTotals(statsResponse.data.totals || null);
    }

    if (showSpinner) setLoading(false);
  }, [statusFilter, includeTest, selectedProducts]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  // Every school seen across both loaded stats and the current rows, so the dropdown still
  // offers the currently-selected school even if a status/test filter has since hidden it.
  const schoolOptions = useMemo(() => {
    const byId = new Map<string, string>();
    stats.forEach((s) => {
      if (s.team_id) byId.set(s.team_id, s.school_name || s.team_id);
    });
    purchasers.forEach((p) => {
      if (p.team_id && !byId.has(p.team_id)) byId.set(p.team_id, p.school_name || p.team_id);
    });
    return Array.from(byId.entries())
      .map(([team_id, school_name]) => ({ team_id, school_name }))
      .sort((a, b) => a.school_name.localeCompare(b.school_name));
  }, [stats, purchasers]);

  const visiblePurchasers = useMemo(() => {
    const emailQuery = emailFilter.trim().toLowerCase();
    return purchasers.filter((row) => {
      if (schoolFilter && row.team_id !== schoolFilter) return false;
      if (emailQuery && !row.email.toLowerCase().includes(emailQuery)) return false;
      return true;
    });
  }, [purchasers, schoolFilter, emailFilter]);

  const handleExportCsv = () => {
    exportToCsv(
      `purchasers-${new Date().toISOString().slice(0, 10)}.csv`,
      visiblePurchasers,
      [
        { header: 'Email', value: (row) => row.email },
        { header: 'Product', value: (row) => row.product_label },
        { header: 'Purchase Date', value: (row) => row.created_at_iso },
        { header: 'School', value: (row) => row.school_name },
        { header: 'Game', value: (row) => (row.product_label === 'Single Game' ? row.game_name : '') },
        { header: 'Status', value: (row) => row.subscription_status },
      ]
    );
  };

  return (
    <div className="section">
      <h2 className="section-title">
        <Receipt size={24} />
        Purchasers
      </h2>

      <Card
        title="Purchasers by School"
        description="Product purchasers across all schools"
        headerAction={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="outline" onClick={() => fetchData(true)} loading={loading}>
              <RefreshCcw size={16} />
              Refresh
            </Button>
            <Button variant="outline" onClick={handleExportCsv} disabled={visiblePurchasers.length === 0}>
              <Download size={16} />
              Export CSV
            </Button>
          </div>
        }
      >
        {/* Filters */}
        <div style={{ marginBottom: '16px', paddingBottom: '14px', borderBottom: '1px solid rgba(var(--color-border-rgb), 0.12)', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '20px' }}>
            <div>
              <label style={fieldLabelStyle}>School</label>
              <select
                className="form-input"
                value={schoolFilter}
                onChange={(e) => setSchoolFilter(e.target.value)}
                style={{ width: '200px', padding: '7px 34px 7px 10px', fontSize: '13px' }}
              >
                <option value="">All schools</option>
                {schoolOptions.map((s) => (
                  <option key={s.team_id} value={s.team_id}>{s.school_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={fieldLabelStyle}>Email</label>
              <input
                className="form-input"
                type="text"
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
                placeholder="Search by email…"
                style={{ width: '220px', padding: '7px 10px', fontSize: '13px' }}
              />
            </div>

            <div>
              <label style={fieldLabelStyle}>Status</label>
              <select
                className="form-input"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ width: '150px', padding: '7px 34px 7px 10px', fontSize: '13px' }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <label style={{ ...checkboxLabelStyle, height: '34px' }}>
              <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} />
              Show test accounts
            </label>
          </div>

          <div>
            <label style={fieldLabelStyle}>Product</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px', padding: '9px 12px', background: Colors.background, borderRadius: '8px' }}>
              <label style={{ ...checkboxLabelStyle, fontWeight: 700 }}>
                <input type="checkbox" checked={isAllPassSelected} onChange={() => setSelectedProducts([])} />
                All Pass
              </label>
              {PRODUCT_OPTIONS.map((label) => (
                <label key={label} style={checkboxLabelStyle}>
                  <input type="checkbox" checked={selectedProducts.includes(label)} onChange={() => toggleProduct(label)} />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-warning" style={{ marginBottom: '12px' }}>
            <AlertCircle size={20} />
            <div>{error}</div>
          </div>
        )}

        {/* Stats strip: one compact tile per school (+ an "All schools" total), horizontally
            scrollable so the row stays a single fixed-height line regardless of school count. */}
        {(totals || stats.length > 0) && (
          <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', padding: '4px 4px 8px', marginBottom: '8px' }}>
            {totals && (
              <button
                type="button"
                title={`By product — ${breakdownLabel(totals.by_product)}`}
                onClick={() => setSchoolFilter('')}
                className={`purchaser-school-tile ${schoolFilter ? '' : 'purchaser-school-tile--selected'}`}
                style={{
                  flex: '0 0 auto', minWidth: '130px',
                  cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'inherit',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: schoolFilter ? Colors.textLight : 'rgba(255, 255, 255, 0.75)' }}>
                  <Users size={12} />
                  All schools
                </div>
                <div style={{ fontSize: '19px', fontWeight: 700, color: schoolFilter ? Colors.text : '#FFFFFF' }}>{totals.total}</div>
              </button>
            )}
            {stats.map((s) => {
              const selected = Boolean(s.team_id) && schoolFilter === s.team_id;
              const clickable = Boolean(s.team_id);
              return (
                <button
                  type="button"
                  key={s.team_id || s.school_name}
                  title={`${s.school_name || 'Unknown'} — ${breakdownLabel(s.by_product)}`}
                  onClick={() => {
                    if (s.team_id) setSchoolFilter(s.team_id);
                  }}
                  disabled={!clickable}
                  className={`purchaser-school-tile ${selected ? 'purchaser-school-tile--selected' : ''}`}
                  style={{
                    flex: '0 0 auto', minWidth: '130px', maxWidth: '160px',
                    cursor: clickable ? 'pointer' : 'default',
                    textAlign: 'left',
                    font: 'inherit',
                    color: 'inherit',
                  }}
                >
                  <div style={{ fontSize: '11px', fontWeight: 700, color: selected ? '#FFFFFF' : Colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.school_name || 'Unknown'}
                  </div>
                  <div style={{ fontSize: '19px', fontWeight: 700, color: selected ? '#FFFFFF' : Colors.text }}>{s.total}</div>
                </button>
              );
            })}
          </div>
        )}

        {/* Purchasers table - fixed column widths (no wrap) so long values ellipsize instead of
            overlapping the next column, and a capped height with a sticky header so the row
            list scrolls in place rather than growing the page. */}
        <div style={{ border: '1px solid rgba(var(--color-border-rgb), 0.16)', borderRadius: '8px', overflow: 'auto', maxHeight: '440px' }}>
          <table className="table" style={{ marginBottom: 0, minWidth: '760px', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '24%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '14%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ position: 'sticky', top: 0, background: 'var(--color-card-background, #fff)', zIndex: 1 }}>Email</th>
                <th style={{ position: 'sticky', top: 0, background: 'var(--color-card-background, #fff)', zIndex: 1 }}>Product</th>
                <th style={{ position: 'sticky', top: 0, background: 'var(--color-card-background, #fff)', zIndex: 1 }}>Purchase Date</th>
                <th style={{ position: 'sticky', top: 0, background: 'var(--color-card-background, #fff)', zIndex: 1 }}>School</th>
                <th style={{ position: 'sticky', top: 0, background: 'var(--color-card-background, #fff)', zIndex: 1 }}>Game</th>
                <th style={{ position: 'sticky', top: 0, background: 'var(--color-card-background, #fff)', zIndex: 1 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {visiblePurchasers.map((row, i) => (
                <tr key={`${row.user_id}-${row.product_id}-${i}`}>
                  <td title={row.email} style={{ fontFamily: "'SF Mono', monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.email || '—'}</td>
                  <td title={row.product_label} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.product_label}</td>
                  <td style={{ fontFamily: "'SF Mono', monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatPurchaseDate(row)}</td>
                  <td title={row.school_name} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.school_name || '—'}</td>
                  <td title={row.game_name} style={{ overflow: 'hidden' }}>
                    {row.product_label === 'Single Game' ? (
                      row.game_name ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', maxWidth: '100%' }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.game_name}</span>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(row.game_name, 'Game name copied')}
                            title="Copy game name"
                            style={{
                              flex: 'none', width: '20px', height: '20px', padding: 0, border: 0, background: 'transparent',
                              display: 'grid', placeItems: 'center', cursor: 'pointer', color: Colors.inactive, borderRadius: '4px',
                            }}
                          >
                            <Copy size={13} />
                          </button>
                        </span>
                      ) : (
                        <span style={{ color: Colors.inactive }}>Unknown game</span>
                      )
                    ) : (
                      <span style={{ color: Colors.inactive }}>—</span>
                    )}
                  </td>
                  <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.subscription_status ? (
                      <span
                        className={`status-badge ${row.subscription_status === 'ACTIVE' ? 'status-success' : row.subscription_status === 'EXPIRED' ? 'status-error' : 'status-warning'}`}
                        style={{ fontSize: '11px', padding: '2px 9px' }}
                      >
                        {row.subscription_status}
                      </span>
                    ) : (
                      <span style={{ color: Colors.inactive }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {visiblePurchasers.length === 0 && !loading && !error && (
          <div style={{ textAlign: 'center', padding: '32px', color: Colors.textLight }}>
            <Receipt size={24} style={{ opacity: 0.4, marginBottom: '6px' }} />
            <div style={{ fontSize: '13px' }}>No purchasers match these filters.</div>
          </div>
        )}
      </Card>
    </div>
  );
}
